import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./Catalogue.css";
import { FaFacebook, FaWhatsapp } from "react-icons/fa";
import { API_BASE_URL } from "../config/api";
import Seo from "../Components/Seo";
import { authedFetch } from "../services/apiClient";

export default function Catalogue() {
    const navigate = useNavigate();

    /* ---------------- STATE ---------------- */
    const [products, setProducts] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [search, setSearch] = useState("");
    const [selectMode, setSelectMode] = useState(false);
    const [selected, setSelected] = useState([]);

    const [filterOpen, setFilterOpen] = useState(false);
    const [minPrice, setMinPrice] = useState("");
    const [maxPrice, setMaxPrice] = useState("");
    const [isFiltered, setIsFiltered] = useState(false);
    const [showShare, setShowShare] = useState(false);
    // Category filter state
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [adminNumbers, setAdminNumbers] = useState([]);

    const mobileNo = localStorage.getItem("userMobileNumber") || "";
    const isAdmin = adminNumbers.includes(mobileNo);

    /* ---------------- FETCH ---------------- */
    useEffect(() => {
        fetchProducts();
        fetchAdmins();
    }, []);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            setError("");

            const res = await authedFetch(
                `${API_BASE_URL}/Product/GetProductsForCat`
            );
            const data = await res.json();

            if (!Array.isArray(data)) throw new Error("Invalid response");

            const filteredData = data.filter((p) => !p.inBin);
            setProducts(filteredData);
            setAllProducts(filteredData);
        } catch (err) {
            setError("Failed to load catalogue. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = useCallback(async () => {
        if (categoriesLoading || categories.length) return;
        try {
            setCategoriesLoading(true);
            const res = await fetch(`${API_BASE_URL}/Category/GetCategories`);
            const data = await res.json();
            if (Array.isArray(data)) {
                setCategories(data);
            }
        } catch (err) {
            console.error("Failed to load categories", err);
        } finally {
            setCategoriesLoading(false);
        }
    }, [categories, categoriesLoading]);

    const fetchAdmins = async () => {
        try {
            const res = await authedFetch(`${API_BASE_URL}/Admin/GetAllAdmins`);
            if (!res.ok) throw new Error("Admin fetch failed");
            const data = await res.json();
            if (Array.isArray(data)) {
                const numbers = data
                    .map((a) => a?.MobileNumber)
                    .filter(Boolean)
                    .map(String);
                setAdminNumbers(numbers);
            }
        } catch (err) {
            console.error("Failed to load admins", err);
            setAdminNumbers([]);
        }
    };

    useEffect(() => {
        if (filterOpen) {
            fetchCategories();
        }
    }, [filterOpen, fetchCategories]);

    /* ---------------- SEARCH + FILTER PIPELINE ---------------- */
    const visibleProducts = useMemo(() => {
        let list = products;

        if (search.trim()) {
            const terms = search.toLowerCase().split(" ");
            list = list.filter((p) =>
                terms.some((t) =>
                    (p.ProductName || "").toLowerCase().includes(t)
                )
            );
        }

        return list;
    }, [products, search]);

    /* ---------------- FILTER ---------------- */
    const applyFilter = () => {
        const min = minPrice ? Number(minPrice) : 0;
        const max = maxPrice ? Number(maxPrice) : Infinity;

        if (min > max) {
            alert("Invalid price range");
            return;
        }

        const filtered = allProducts.filter((p) => {
            const price = Number(p.Price || 0);
            const inPriceRange = price >= min && price <= max;
            const productCategoryId = p.CategoryId ?? p.categoryId ?? p.CategoryID ?? p.Category?.Id ?? p.Category?.id;
            const categoryMatches = selectedCategory ? String(productCategoryId) === String(selectedCategory) : true;
            return inPriceRange && categoryMatches;
        });

        setProducts(filtered);
        setIsFiltered(Boolean(minPrice || maxPrice || selectedCategory));
        setFilterOpen(false);
    };

    const clearFilter = () => {
        setProducts(allProducts);
        setMinPrice("");
        setMaxPrice("");
        setSelectedCategory("");
        setIsFiltered(false);
        setFilterOpen(false);
    };

    /* ---------------- SELECTION ---------------- */
    const toggleSelect = (id) => {
        setSelected((prev) =>
            prev.includes(id)
                ? prev.filter((x) => x !== id)
                : [...prev, id]
        );
    };


    /* ---------------- DELETE ---------------- */
    const deleteProduct = async (id) => {
        if (!isAdmin) {
            alert("You are not authorized");
            return;
        }

        if (!window.confirm("Want to move this product to bin?")) return;

        const res = await authedFetch(
            `${API_BASE_URL}/Product/DeleteProduct?id=${id}`,
            { method: "DELETE" }
        );
        if (!res.ok) {
            alert("Delete failed — server ne reject kiya");
            return;
        }

        setProducts((p) => p.filter((x) => x.Id !== id));
        setAllProducts((p) => p.filter((x) => x.Id !== id));
    };

    const deleteSelected = async () => {
        if (!isAdmin) {
            alert("You are not authorized");
            return;
        }

        if (!window.confirm(`Delete ${selected.length} products?`)) return;

        for (const id of selected) {
            const res = await authedFetch(
                `${API_BASE_URL}/Product/DeleteProduct?id=${id}`,
                { method: "DELETE" }
            );
            if (!res.ok) {
                alert(`Deleting id=${id} fail hua`);
                break;
            }
        }

        setProducts((p) => p.filter((x) => !selected.includes(x.Id)));
        setAllProducts((p) => p.filter((x) => !selected.includes(x.Id)));
        setSelected([]);
        setSelectMode(false);
    };

    /* ---------------- SHARE ---------------- */
    const shareSingle = async (product) => {
        const text = `${product.ProductName}
₹${product.Price}

${product.Description || ""}

https://facebookindia.in/product/${product.Id}`;

        if (navigator.share) {
            await navigator.share({ text });
        } else {
            window.open(
                `https://wa.me/?text=${encodeURIComponent(text)}`,
                "_blank"
            );
        }
    };
    const [selectedProduct, setSelectedProduct] = useState(null);

    // Normalize product fields for sharing
    const normalizeProduct = (p) => ({
        id: p?.Id ?? p?.id,
        name: p?.ProductName ?? p?.name ?? "",
        description: p?.Description ?? p?.description ?? "",
        price: p?.Price ?? p?.price ?? "",
        image: p?.Image1 ?? p?.ProductImage ?? p?.image ?? "",
    });

    const getCanonicalProductUrl = (id) => {
        const base = process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin;
        return `${base}/catalogue-product-details/${id}`;
    };

    const openFacebookShare = (product) => {
        const prod = normalizeProduct(product);
        const pageUrl = getCanonicalProductUrl(prod.id);
        const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        window.open(url, "_blank", "noopener,noreferrer");
        setShowShare(false);
    };

    const openWhatsAppShare = (product) => {
        const prod = normalizeProduct(product);
        const pageUrl = getCanonicalProductUrl(prod.id);
        const lines = [
            prod.name && `Product: ${prod.name}`,
            prod.price !== "" && `Price: ₹${prod.price}`,
            prod.description && `Description: ${prod.description}`,
            prod.image && `Image: ${prod.image}`,
            `Details: ${pageUrl}`,
        ].filter(Boolean);
        const text = encodeURIComponent(lines.join("\n"));
        const isMobile = /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
            navigator.userAgent
        );
        const waUrl = isMobile
            ? `https://wa.me/?text=${text}`
            : `https://web.whatsapp.com/send?text=${text}`;
        window.open(waUrl, "_blank", "noopener,noreferrer");
        setShowShare(false);
    };

    const handleAllShare = (product) => {
        setShowShare(true);
        setSelectedProduct(product.Id);

    }

    const shareMultiple = async () => {
        const text = selected
            .map((id) => {
                const p = products.find((x) => x.Id === id);
                if (!p) return "";
                return `• ${p.ProductName} – ₹${p.Price}
https://facebookindia.in/product/${p.Id}`;
            })
            .join("\n\n");

        const finalText = `Check out these products:\n\n${text}`;

        if (navigator.share) {
            await navigator.share({ text: finalText });
        } else {
            window.open(
                `https://wa.me/?text=${encodeURIComponent(finalText)}`,
                "_blank"
            );
        }
    };
    // const [sortedProductsDesc, setSortedProductsDesc] = useState([]);
    // if (visibleProducts) {
    const sortedProductsDesc = [...visibleProducts]?.sort(
        (a, b) => b.Id - a.Id
    );
    //     setSortedProductsDesc(sortedProducts);
    // }


    /* ---------------- UI STATES ---------------- */
    if (loading) return <div className="center">Loading…</div>;

    if (error)
        return (
            <div className="center">
                <p>{error}</p>
                <button onClick={fetchProducts}>Retry</button>
            </div>
        );

    /* ---------------- RENDER ---------------- */
    return (
        <div className="catalogue-page">
            <Seo
                title="Browse Products & Categories"
                description="AIO India ke poore catalogue mein search karein — Mobiles, Electronics, Cars, Bikes, Furniture, Jobs aur baaki sab. Best deals sirf AIO India pe."
                path="/catalogue"
            />
            {/* HEADER */}
            <header className="catalogue-header">
                <input
                    placeholder="Search products…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />

                {!selectMode ? (
                    <div className="header-actions">
                        <button onClick={() => setFilterOpen(true)}>
                            {isFiltered ? "Filtered" : "Filter"}
                        </button>
                        {/* <button onClick={() => setSelectMode(true)}>Select</button> */}
                    </div>
                ) : (
                    <div className="header-actions">
                        <button onClick={() => setSelectMode(false)}>Cancel</button>
                        {/* <button onClick={selectAll}>
                            {selected.length === visibleProducts.length
                                ? "Deselect All"
                                : "Select All"}
                        </button> */}
                    </div>
                )}
            </header>

            {/* GRID */}
            <div className="grid">
                {sortedProductsDesc?.map((item) => {
                    const checked = selected.includes(item.Id);

                    return (
                        <div
                            key={item.Id}
                            className={`card ${checked ? "selected" : ""}`}
                            onClick={() =>
                                selectMode
                                    ? toggleSelect(item.Id)
                                    : navigate(`/catalogue-product-details/${item.Id}`)
                            }
                        >
                            {selectMode && (
                                <div className={`checkbox ${checked ? "on" : ""}`} />
                            )}

                            <img
                                src={item.ProductImage || "/placeholder.png"}
                                alt={item.ProductName}
                            />

                            <div className="info">
                                <h4>{item.ProductName}</h4>
                                <span>₹{item.Price}</span>
                                {item.Quantity > 0 && (
                                    <span style={{ marginLeft: "6px", fontWeight: 600, fontSize: "12px", color: "#475569" }}>
                                        Qty: {item.Quantity}
                                    </span>
                                )}
                            </div>

                            {!selectMode && (
                                <div className="actions">
                                    {isAdmin && (
       <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/edit-product/${item.Id}`);
                                        }}
                                    >
                                        Edit
                                    </button>
                                    ) }
                             

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            // shareSingle(item);
                                            handleAllShare(item);
                                        }}
                                    >
                                        Share
                                    </button>
                                    {(showShare && item.Id===selectedProduct) && (
                                        <div style={styles.sharePopup}>
                                            <button style={styles.shareItem} 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openFacebookShare(item);
                                            }}
                                            aria-label="Share to Facebook">
                                                <FaFacebook color="#1877F2" size={18} />
                                                <span style={styles.shareText}>Facebook</span>
                                            </button>
                                            <button style={styles.shareItem} 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openWhatsAppShare(item);
                                            }}
                                             aria-label="Share to WhatsApp">
                                                <FaWhatsapp color="#25D366" size={18} />
                                                <span style={styles.shareText}>WhatsApp</span>
                                            </button>
                                            {/* <button style={styles.shareItem} onClick={() => onShareAction("Gmail")} aria-label="Share via Gmail">
                                                          <SiGmail color="#EA4335" size={18} />
                                                          <span style={styles.shareText}>Gmail</span>
                                                        </button> */}
                                        </div>
                                    )}

                                    {isAdmin && (
                                        <button
                                            className="danger"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                deleteProduct(item.Id);
                                            }}
                                        >
                                            Delete
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* EMPTY STATE */}
            {visibleProducts.length === 0 && (
                <div className="empty">
                    <h3>No products found</h3>
                    {isFiltered && <button onClick={clearFilter}>Clear Filters</button>}
                </div>
            )}

            {/* BOTTOM BAR */}
            {selectMode && selected.length > 0 && (
                <div className="bottom-bar">
                    <button onClick={shareMultiple}>Share</button>
                    {isAdmin && (
                        <button className="danger" onClick={deleteSelected}>
                            Delete
                        </button>
                    )}
                </div>
            )}

            {/* FILTER MODAL */}
            {filterOpen && (
                <div className="modal">
                    <div className="modal-content">
                        <h3>Filter by Price & Category</h3>

                        <input
                            placeholder="Min price"
                            value={minPrice}
                            onChange={(e) => setMinPrice(e.target.value)}
                        />
                        <input
                            placeholder="Max price"
                            value={maxPrice}
                            onChange={(e) => setMaxPrice(e.target.value)}
                        />

                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ccc" }}
                        >
                            <option value="">All categories</option>
                            {categoriesLoading && <option disabled>Loading...</option>}
                            {categories.map((c) => (
                                <option key={c.id ?? c.Id} value={c.id ?? c.Id}>
                                    {c.name ?? c.Name}
                                </option>
                            ))}
                        </select>

                        <div className="modal-actions">
                            <button onClick={clearFilter}>Clear</button>
                            <button onClick={applyFilter}>Apply</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
/* 🔹 STYLES (INLINE FOR SINGLE FILE USAGE) */
const styles = {
  page: {
    backgroundColor: "#f1f3f6",
    minHeight: "100vh",
    padding: "30px",
  },
  container: {
    maxWidth: "1200px",
    margin: "auto",
    background: "#fff",
    display: "flex",
    gap: "40px",
    padding: "30px",
    borderRadius: "6px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  },
  left: {
    width: "40%",
  },
  imageWrapper: {
    position: "relative",
    border: "1px solid #ddd",
    borderRadius: "6px",
    padding: "15px",
  },
  image: {
    width: "100%",
    objectFit: "contain",
  },
  icons: {
    position: "absolute",
    top: "15px",
    right: "15px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  icon: {
    background: "#fff",
    padding: "10px",
    borderRadius: "50%",
    cursor: "pointer",
    boxShadow: "0 1px 5px rgba(0,0,0,0.2)",
  },
  sharePopup: {
    position: "absolute",
    top: "40px",
    right: 0,
    background: "#fff",
    border: "1px solid #e0e0e0",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    borderRadius: "8px",
    padding: "8px",
    minWidth: "160px",
    zIndex: 5,
  },
  shareItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    background: "transparent",
    border: "none",
    padding: "8px",
    borderRadius: "6px",
    cursor: "pointer",
    textAlign: "left",
  },
  shareText: {
    fontSize: "14px",
    color: "#333",
  },
  buttonGroup: {
    display: "flex",
    gap: "15px",
    marginTop: "20px",
  },
  button: {
    flex: 1,
    padding: "14px",
    fontSize: "16px",
    fontWeight: "bold",
    cursor: "pointer",
    border: "none",
    borderRadius: "4px",
  },
  cartBtn: {
    background: "#ff9f00",
    color: "#fff",
  },
  buyBtn: {
    background: "#fb641b",
    color: "#fff",
  },
  right: {
    width: "60%",
  },
  title: {
    fontSize: "26px",
    marginBottom: "15px",
  },
  price: {
    fontSize: "24px",
    color: "#388e3c",
    fontWeight: "bold",
    marginBottom: "15px",
  },
  desc: {
    fontSize: "16px",
    color: "#333",
    marginBottom: "15px",
  },
  mobile: {
    fontSize: "15px",
    marginBottom: "10px",
  },
  time: {
    fontSize: "14px",
    color: "#555",
  },
  timeAgo: {
    color: "#878787",
    marginLeft: "6px",
  },
  center: {
    textAlign: "center",
    marginTop: "100px",
    fontSize: "18px",
  },
  error: {
    textAlign: "center",
    marginTop: "100px",
    color: "red",
  },
};
